import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Edit,
  Upload,
  FileText,
  Calendar,
  DollarSign,
  User,
  Tag,
  Image,
  Trash2,
  Eye,
} from "lucide-react";
import { API_ENDPOINTS } from "../config/api";
import apiRequest from "../utils/api";
import { FileUploadModal } from "../components/FileUploadModal";
import { getCategoryLabel } from "../utils/categoryMapping";

interface Product {
  id: number;
  title: string;
  author: string;
  price: number;
  category: string;
  imageUrl: string;
  description: string;
  type: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface ProductContent {
  id: number;
  title: string;
  description: string;
  contentType: string;
  createdAt?: string;
  updatedAt?: string;
}

export const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [contents, setContents] = useState<ProductContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFileUpload, setShowFileUpload] = useState(false);

  const fetchProductDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`${API_ENDPOINTS.PRODUCTS}/${id}`, {
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProduct(data.data);
        }
      }
    } catch (error) {
      console.error("상품 상세 조회 실패:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchProductContents = useCallback(async () => {
    try {
      const response = await apiRequest(
        API_ENDPOINTS.PRODUCT_CONTENTS.LIST(id!),
        {
          method: "GET",
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setContents(data.data);
        }
      }
    } catch (error) {
      console.error("상품 컨텐츠 조회 실패:", error);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchProductDetail();
      fetchProductContents();
    }
  }, [id, fetchProductDetail, fetchProductContents]);

  const handleFileUpload = () => {
    setShowFileUpload(true);
  };

  const handleCloseModal = () => {
    setShowFileUpload(false);
  };

  const handleUploadSuccess = () => {
    fetchProductContents();
    handleCloseModal();
  };

  const handleDeleteContent = async (contentId: number) => {
    if (window.confirm("정말로 이 컨텐츠를 삭제하시겠습니까?")) {
      try {
        const response = await apiRequest(
          API_ENDPOINTS.PRODUCT_CONTENTS.DELETE(contentId.toString()),
          {
            method: "DELETE",
          }
        );

        if (response.ok) {
          fetchProductContents();
        }
      } catch (error) {
        console.error("컨텐츠 삭제 실패:", error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">로딩 중...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">상품을 찾을 수 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/course-management")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            목록으로
          </Button>
          <div className="flex items-center space-x-2">
            <Image className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              {product.title}
            </h1>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline">
            <Edit className="h-4 w-4 mr-2" />
            수정
          </Button>
          <Button onClick={handleFileUpload}>
            <Upload className="h-4 w-4 mr-2" />
            컨텐츠 등록
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 상품 정보 */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>상품 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-4">
                <img
                  src={product.imageUrl}
                  alt={product.title}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h2 className="text-xl font-semibold">{product.title}</h2>
                  <div className="flex items-center space-x-2 mt-2">
                    <Badge variant="secondary">
                      {getCategoryLabel(product.category)}
                    </Badge>
                    <Badge
                      variant={
                        product.type === "PURCHASE" ? "default" : "outline"
                      }
                    >
                      {product.type === "PURCHASE" ? "유료" : "무료"}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">저자:</span>
                  <span className="font-medium">{product.author}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">가격:</span>
                  <span className="font-medium">
                    {product.price.toLocaleString()}원
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">등록일:</span>
                  <span className="font-medium">
                    {new Date(product.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">수정일:</span>
                  <span className="font-medium">
                    {new Date(product.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {product.tags && product.tags.length > 0 && (
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">태그:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.tags.map((tag, index) => (
                      <Badge key={index} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-medium mb-2">상품 설명</h3>
                <p className="text-sm text-muted-foreground">
                  {product.description}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 컨텐츠 목록 */}
          <Card>
            <CardHeader>
              <CardTitle>업로드된 컨텐츠</CardTitle>
            </CardHeader>
            <CardContent>
              {contents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  업로드된 컨텐츠가 없습니다.
                </div>
              ) : (
                <div className="space-y-3">
                  {contents.map((content) => (
                    <div
                      key={content.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{content.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {content.contentType} •{" "}
                            {content.createdAt &&
                              new Date(content.createdAt).toLocaleDateString()}
                          </p>
                          {content.description && (
                            <p className="text-xs text-gray-500 mt-1">
                              {content.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button size="sm" variant="outline">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteContent(content.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* 통계 및 액션 */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>컨텐츠 통계</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{contents.length}</div>
                <p className="text-sm text-muted-foreground">업로드된 컨텐츠</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>빠른 액션</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full" onClick={handleFileUpload}>
                <Upload className="h-4 w-4 mr-2" />새 컨텐츠 등록
              </Button>
              <Button variant="outline" className="w-full">
                <Edit className="h-4 w-4 mr-2" />
                상품 정보 수정
              </Button>
              <Button variant="outline" className="w-full">
                <Eye className="h-4 w-4 mr-2" />
                상품 미리보기
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 파일 업로드 모달 */}
      {showFileUpload && product && (
        <FileUploadModal
          product={product}
          onClose={handleCloseModal}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};
